import { CalibrationPoint, Ramp } from "../../../../../types";

export interface ParsedGISRow {
  id?: string;
  airport?: string;
  tenant?: string;
  ramp_label: string;
  polygon: number[][];
  // Optional shape attributes
  type?: string;
  locked?: boolean;
  stroke_style?: string;
  line_style?: string;
  line_width?: number;
  fill_style?: string;
  fill_pattern?: string;
  text?: string;
  font_size?: number;
}

/**
 * Parse CSV or TSV content containing GIS polygon data
 * Auto-detects format based on header line delimiter
 */
export function parseGISCSV(csvContent: string): ParsedGISRow[] {
  const lines = csvContent.trim().split("\n");

  if (lines.length < 2) {
    throw new Error("Data must have at least a header row and one data row");
  }

  const headerLine = lines[0];

  // Auto-detect delimiter (CSV vs TSV)
  const delimiter = detectDelimiter(headerLine);
  const headers = parseDelimitedLine(headerLine, delimiter).map((h) =>
    h.toLowerCase().trim()
  );

  // Required columns
  const requiredColumns = ["ramp_label", "ramp_polygon"];

  // Check that required columns exist
  for (const required of requiredColumns) {
    if (!headers.includes(required)) {
      throw new Error(`Missing required column: ${required}`);
    }
  }

  // Map header names to column indices
  const columnMap: Record<string, number> = {};
  headers.forEach((header, index) => {
    columnMap[header] = index;
  });

  // Optional shape attribute columns with their default values
  const optionalColumns = {
    id: null,
    airport: null,
    tenant: null,
    type: "Area",
    locked: false,
    stroke_style: "#3c6ce9",
    line_style: "solid",
    line_width: 2,
    fill_style: "rgba(60, 108, 233, 0.1)",
    fill_pattern: null,
    text: null,
    font_size: 14,
  };

  const results: ParsedGISRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    try {
      const line = lines[i].trim();
      if (!line) continue;

      const parsed = parseDelimitedLine(line, delimiter);

      // Get required fields
      const ramp_label =
        parsed[columnMap["ramp_label"]]?.trim() || `Shape ${i}`;
      const ramp_polygon_str = parsed[columnMap["ramp_polygon"]]?.trim();

      if (!ramp_polygon_str) {
        console.warn(`Skipping line ${i + 1}: missing ramp_polygon`);
        continue;
      }

      // Parse the polygon coordinates
      let polygon: number[][];
      try {
        // Remove quotes and parse JSON
        const polygonStr = ramp_polygon_str.replace(/^"(.*)"$/, "$1");
        polygon = JSON.parse(polygonStr);

        if (!Array.isArray(polygon) || polygon.length === 0) {
          throw new Error("Invalid polygon format");
        }

        // Validate coordinate format
        for (const coord of polygon) {
          if (!Array.isArray(coord) || coord.length !== 2) {
            throw new Error("Each coordinate must be [longitude, latitude]");
          }
        }
      } catch (err) {
        console.warn(
          `Skipping line ${i + 1}: invalid polygon data - ${err.message}`
        );
        continue;
      }

      // Build the result object with required fields
      const row: ParsedGISRow = {
        ramp_label,
        polygon,
      };

      // Add optional fields if they exist in the CSV
      Object.keys(optionalColumns).forEach((key) => {
        const columnIndex = columnMap[key];
        if (columnIndex !== undefined && parsed[columnIndex] !== undefined) {
          const value = parsed[columnIndex]?.trim();
          if (value !== undefined && value !== "") {
            // Type conversion based on the field
            switch (key) {
              case "locked":
                row[key] = value.toLowerCase() === "true" || value === "1";
                break;
              case "line_width":
              case "font_size":
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                  row[key] = numValue;
                }
                break;
              default:
                row[key] = value;
            }
          }
        }
      });

      // Apply defaults for any missing optional fields
      Object.keys(optionalColumns).forEach((key) => {
        if (row[key] === undefined) {
          const defaultValue = optionalColumns[key];
          if (defaultValue !== null) {
            row[key] = defaultValue;
          } else if (key === "fill_pattern") {
            // fill_pattern should be undefined when not specified
            row[key] = undefined;
          }
        }
      });

      // Special handling for text field - use ramp_label as default if empty
      if (!row.text) {
        row.text = ramp_label;
      }

      results.push(row);
    } catch (err) {
      console.warn(`Skipping line ${i + 1}: ${err.message}`);
    }
  }

  if (results.length === 0) {
    throw new Error("No valid polygon data found in the uploaded data");
  }

  return results;
}

/**
 * Detect delimiter (comma or tab) based on the header line
 */
function detectDelimiter(line: string): string {
  // Count occurrences of comma and tab outside of quotes
  let commaCount = 0;
  let tabCount = 0;
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (!inQuotes) {
      if (char === ",") {
        commaCount++;
      } else if (char === "\t") {
        tabCount++;
      }
    }
  }

  // Return the delimiter with higher count, default to comma
  return tabCount > commaCount ? "\t" : ",";
}

/**
 * Parse a delimited line (CSV or TSV) handling quoted values
 */
function parseDelimitedLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      // Handle escaped quotes (double quotes inside quotes)
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        current += '"';
        i++; // Skip the next quote
      } else {
        inQuotes = !inQuotes;
      }
      // Don't include the quote delimiters in the output
    } else if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}

/**
 * Convert GIS polygon coordinates to ramp coordinate system
 * This matches the coordinate system used by RampCanvas.tsx where:
 * - stored_coordinate = pixel_coordinate / pixelsToFeetRatio
 * - pixelsToFeetRatio = canvasWidth / rampWidth
 *
 * Uses calibration points to establish an accurate transformation from GPS coordinates to pixel coordinates
 */
export function convertGISPolygonToPixels(
  polygon: number[][],
  calibrationPoints: CalibrationPoint[],
  rampWidth: number,
  rampDepth: number,
  canvasWidth: number
): { x: number; y: number }[] {
  if (calibrationPoints.length < 2) {
    throw new Error(
      "Need at least 2 calibration points for coordinate conversion"
    );
  }

  try {
    // Calculate the pixels to feet ratio used by the ramp system
    const pixelsToFeetRatio = canvasWidth / rampWidth;
    const canvasHeight = (canvasWidth * rampDepth) / rampWidth;

    return polygon.map(([lng, lat]) => {
      // Transform GPS coordinates to pixel coordinates using calibration points
      // Note: polygon coordinates come as [longitude, latitude] but we need to pass (latitude, longitude)
      const { pixelX, pixelY } = transformGPSToPixelSimple(
        lat,
        lng,
        calibrationPoints,
        canvasWidth,
        canvasHeight
      );

      // Convert to ramp coordinate system (stored coordinates = pixels / pixelsToFeetRatio)
      const x = pixelX / pixelsToFeetRatio;
      const y = pixelY / pixelsToFeetRatio;

      return {
        x: Math.round(x * 100) / 100,
        y: Math.round(y * 100) / 100,
      };
    });
  } catch (err) {
    console.error("Error converting GIS coordinates:", err);
    throw new Error(`Failed to convert coordinates: ${err.message}`);
  }
}

/**
 * Transform GPS coordinates to pixel coordinates using 2 calibration points
 * This approach creates an affine transformation using the calibration points
 */
function transformGPSToPixelSimple(
  lat: number,
  lng: number,
  calibrationPoints: CalibrationPoint[],
  canvasWidth: number,
  canvasHeight: number
): { pixelX: number; pixelY: number } {
  const point1 = calibrationPoints[0];
  const point2 = calibrationPoints[1];

  // Convert relative coordinates to pixel coordinates
  // Note: Y coordinates need to be inverted because canvas Y increases downward
  // but the ramp system uses Y increasing upward (like GPS)
  const point1PixelX = point1.x * canvasWidth;
  const point1PixelY = (1 - point1.y) * canvasHeight; // Invert Y
  const point2PixelX = point2.x * canvasWidth;
  const point2PixelY = (1 - point2.y) * canvasHeight; // Invert Y

  // Use a more robust GPS coordinate conversion
  // Convert all coordinates to a local tangent plane in meters
  const earthRadius = 6378137; // WGS84 Earth radius in meters
  const toRadians = (deg: number) => (deg * Math.PI) / 180;

  // Reference point (use first calibration point as origin)
  const refLat = point1.latitude;
  const refLng = point1.longitude;
  const refLatRad = toRadians(refLat);

  // Convert GPS coordinates to local meters (tangent plane projection)
  const convertToLocalMeters = (lat: number, lng: number) => {
    const latRad = toRadians(lat);
    const lngRad = toRadians(lng);
    const refLngRad = toRadians(refLng);

    const x = earthRadius * (lngRad - refLngRad) * Math.cos(refLatRad);
    const y = earthRadius * (latRad - refLatRad);

    return { x, y };
  };

  // Convert calibration points and target point to local meters
  const point1Meters = convertToLocalMeters(point1.latitude, point1.longitude); // Should be (0, 0)
  const point2Meters = convertToLocalMeters(point2.latitude, point2.longitude);
  const targetMeters = convertToLocalMeters(lat, lng);

  // Calculate the transformation matrix from GPS meters to pixel coordinates
  // We have two corresponding point pairs: (point1Meters, point1Pixel) and (point2Meters, point2Pixel)

  const metersVectorX = point2Meters.x - point1Meters.x;
  const metersVectorY = point2Meters.y - point1Meters.y;
  const pixelVectorX = point2PixelX - point1PixelX;
  const pixelVectorY = point2PixelY - point1PixelY;

  const metersDistance = Math.sqrt(
    metersVectorX * metersVectorX + metersVectorY * metersVectorY
  );
  const pixelDistance = Math.sqrt(
    pixelVectorX * pixelVectorX + pixelVectorY * pixelVectorY
  );

  if (metersDistance === 0) {
    throw new Error("Calibration points are at the same GPS location");
  }

  const scale = pixelDistance / metersDistance;

  // Calculate rotation angle
  const metersAngle = Math.atan2(metersVectorY, metersVectorX);
  const pixelAngle = Math.atan2(pixelVectorY, pixelVectorX);
  const rotation = pixelAngle - metersAngle;

  // Transform target point from meters to pixels
  const targetVectorX = targetMeters.x - point1Meters.x;
  const targetVectorY = targetMeters.y - point1Meters.y;

  // Apply rotation and scaling
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  const rotatedX = targetVectorX * cos - targetVectorY * sin;
  const rotatedY = targetVectorX * sin + targetVectorY * cos;

  // Scale and translate to final pixel coordinates
  const pixelX = point1PixelX + rotatedX * scale;
  const pixelY = point1PixelY + rotatedY * scale;

  // Invert Y back to ramp coordinate system (Y increases upward)
  const rampPixelY = canvasHeight - pixelY;

  return { pixelX, pixelY: rampPixelY };
}

/**
 * Validate that converted coordinates are reasonable for the ramp
 */
export function validatePixelCoordinates(
  points: { x: number; y: number }[],
  rampWidth: number = 1000,
  rampDepth: number = 600
): boolean {
  for (const point of points) {
    // Check if coordinates are within reasonable bounds for the ramp coordinate system
    // Since stored coordinates = pixels / pixelsToFeetRatio, reasonable values would be
    // roughly in the range of 0 to rampWidth (for the coordinate system scale)
    if (
      point.x < -rampWidth ||
      point.x > rampWidth * 2 ||
      point.y < -rampDepth ||
      point.y > rampDepth * 2
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Check if a GIS polygon is visible within the ramp's canvas bounds
 * Uses the same coordinate transformation as convertGISPolygonToPixels to ensure consistency
 */
export function isShapeWithinRampBounds(
  polygon: number[][],
  ramp: Ramp
): boolean {
  // Need at least 2 calibration points for coordinate transformation
  if (!ramp.calibration_points || ramp.calibration_points.length < 2) {
    // If we can't do coordinate transformation, assume it's visible
    return true;
  }

  try {
    // Convert the GIS polygon to pixel coordinates using the same logic as convertGISPolygonToPixels
    const pixelPoints = convertGISPolygonToPixels(
      polygon,
      ramp.calibration_points,
      ramp.width,
      ramp.depth,
      1000 // Default canvas width used by the ramp editor
    );

    // Use a more generous margin to reduce false positives
    // Many legitimate shapes extend slightly beyond the ramp boundaries
    const margin = Math.max(ramp.width, ramp.depth) * 1.0; // Increased from 0.5 to 1.0 (100% margin)
    const minX = -margin;
    const maxX = ramp.width + margin;
    const minY = -margin;
    const maxY = ramp.depth + margin;

    // Check if any point is within the generous bounds
    for (const point of pixelPoints) {
      if (
        point.x >= minX &&
        point.x <= maxX &&
        point.y >= minY &&
        point.y <= maxY
      ) {
        return true;
      }
    }

    // Check if the shape completely contains the ramp center
    const rampCenterX = ramp.width / 2;
    const rampCenterY = ramp.depth / 2;

    if (
      isPointInPolygon(
        [rampCenterX, rampCenterY],
        pixelPoints.map((p) => [p.x, p.y])
      )
    ) {
      return true;
    }

    // Additional check: If the shape intersects with the core ramp area at all
    // This helps catch shapes that might cross through the ramp even if no single point is within bounds
    const coreRampPolygon = [
      [0, 0],
      [ramp.width, 0],
      [ramp.width, ramp.depth],
      [0, ramp.depth],
    ];

    // Check if any edge of the shape intersects with the ramp bounds
    for (let i = 0; i < pixelPoints.length; i++) {
      const p1 = pixelPoints[i];
      const p2 = pixelPoints[(i + 1) % pixelPoints.length];

      // Check if this edge intersects with any edge of the ramp
      for (let j = 0; j < coreRampPolygon.length; j++) {
        const r1 = coreRampPolygon[j];
        const r2 = coreRampPolygon[(j + 1) % coreRampPolygon.length];

        if (doLinesIntersect([p1.x, p1.y], [p2.x, p2.y], r1, r2)) {
          return true;
        }
      }
    }

    return false;
  } catch (error) {
    console.warn("Failed to check shape bounds, assuming visible:", error);
    // If coordinate transformation fails, assume it's visible to avoid false negatives
    return true;
  }
}

/**
 * Check if a point is inside a polygon using the ray casting algorithm
 */
function isPointInPolygon(point: number[], polygon: number[][]): boolean {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Check if two line segments intersect
 */
function doLinesIntersect(
  line1Start: number[],
  line1End: number[],
  line2Start: number[],
  line2End: number[]
): boolean {
  const [x1, y1] = line1Start;
  const [x2, y2] = line1End;
  const [x3, y3] = line2Start;
  const [x4, y4] = line2End;

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-10) {
    // Lines are parallel
    return false;
  }

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}
